import { interestCreation } from "@bitwarden/web-vault/app/models/types/estimate-action.types";
import {
  EstimateActionProperties,
  ScenarioHelpInfo,
  ScenarioPermutation,
} from "@bitwarden/web-vault/app/models/types/scenario-group.types";
import { InterestRateScenarioOption } from "@bitwarden/web-vault/app/services/DataCalculationService/scenarios/ScenarioOptions/interest-rate-scenario-option";

// Temporary prod debug for zube 14318: opt in per device with
// localStorage.setItem("scenarioDebug", "1") so user balances never hit the console by default.
const isScenarioDebug = () => globalThis.localStorage?.getItem("scenarioDebug") === "1";

export class CurrentInterestRate extends InterestRateScenarioOption {
  /**
   * createPermutations - Create the different possible sets of permutations to run
   *                      By default, this should only run the ones from blobby so that it will
   *                      work for user generated scenarios eventually
   */
  async createPermutations() {
    this.permutations = [];

    if (!this.hasPositiveAccounts) {
      return;
    }

    const accounts = [];
    const accountBalance = [];
    const helpInfo: ScenarioHelpInfo = {};
    const debugAccounts = [];

    const scenarioPermutation: ScenarioPermutation = { estimateActions: [] };
    // The symbol this scenario is interested in
    const symbol = this.scenarioGroup.symbol;
    helpInfo.symbol = symbol;
    if (this.startingAccountBalances) {
      for (const accountID in this.startingAccountBalances) {
        // check for the symbol in the balance greater than 0
        const balance = this.startingAccountBalances[accountID];
        const amount = balance?.[symbol];
        //TODO @Sinan@Michelle: they want to see the negative balance rates as well. aka they want to see the zero rates transactions on the current interest rate scenario
        const account = await this.util.getBookById(accountID);
        const accountUrl = await this.util.getAccountUrl(account);
        const accountInstitutionName = await this.util.getInstitutionName(account);
        // check for the account type being one with interest rates
        let interestRates = await this.util.getInterestRates(account, symbol);

        if (
          (!interestRates || interestRates.length === 0) &&
          this.startingAccountBalances[account.id]?.[symbol] > 0
        ) {
          interestRates = [
            {
              rate: 0,
              symbol: symbol,
              banded: false,
              range: -1,
              last_updated: "",
              condition_intrinsic_desc: "",
              condition_action_desc: "",
            },
          ];
        }
        const included = !!interestRates?.length && amount != null;
        debugAccounts.push({ accountID, name: account?.name, amount, interestRates, included });
        if (included) {
          accountBalance.push(amount);
          accounts.push({
            accountName: account.name,
            rate: interestRates,
            accountUrl: accountUrl,
            accountBank: accountInstitutionName,
          });
          const parameters = this.util.createInterestParameters(account, interestRates, this);
          const permutation: EstimateActionProperties = {
            parameters: parameters,
            estimateActionType: interestCreation,
          };
          scenarioPermutation.estimateActions.push(permutation);
        }
      }
    }
    if (isScenarioDebug()) {
      // eslint-disable-next-line no-console -- temporary prod debug for zube 14318
      console.log("[scenario-diff] current accounts (all starting balances)", debugAccounts);
    }
    if (scenarioPermutation.estimateActions.length > 0) {
      helpInfo.existingAccounts = accounts;
      helpInfo.totalBalance = accountBalance.reduce((a, b) => a + b, 0);
      scenarioPermutation.scenarioHelpInfo = helpInfo;
      this.permutations.push(scenarioPermutation);
    }
  }

  async getBalanceForPermutation(permutation: ScenarioPermutation): Promise<number> {
    const finalBalance = await super.getBalanceForPermutation(permutation);
    if (isScenarioDebug()) {
      // eslint-disable-next-line no-console -- temporary prod debug for zube 14318
      console.log("[scenario-diff] current final balances", {
        finalBalance,
        byAccount:
          permutation.estimateActions[permutation.estimateActions.length - 1]
            ?.runningAccountBalances,
        errors: permutation.estimateActions.map((a) => a.error).filter(Boolean),
      });
    }
    return finalBalance;
  }
}
